import { Form, FormLayout, TextArea, TextField } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import type { SqlImportDraft } from '../draft';

export const DETAILS_FORM_ID = 'sql-import-details-form';

const schema = z.object({
  name: z.string().trim().min(1, "Name can't be blank"),
  description: z.string(),
});

type DetailsFormValues = z.infer<typeof schema>;

type DetailsStepProps = {
  draft: SqlImportDraft;
  isReadOnly: boolean;
  onChange: (changes: Partial<SqlImportDraft>) => void;
  /** The step's form validated; the wizard moves on. */
  onSubmit: () => void;
};

/**
 * The Sync step: a required name and an optional description. The wizard's
 * "Next" is this form's submit button (by form id), so Enter in a field advances
 * the same way a click does.
 */
export function DetailsStep({ draft, isReadOnly, onChange, onSubmit }: DetailsStepProps) {
  const form = useZodForm<DetailsFormValues>({
    schema,
    mode: 'onSubmit',
    defaultValues: { name: draft.name ?? '', description: draft.description ?? '' },
  });
  const name = form.registerPluma('name');
  const description = form.registerPluma('description');

  return (
    <Form
      id={DETAILS_FORM_ID}
      autoLoading={false}
      onSubmit={form.handleSubmit((values) => {
        onChange({ name: values.name.trim(), description: values.description });
        onSubmit();
      })}
    >
      <FormLayout>
        <TextField
          label="Name"
          placeholder="Name your import"
          autoFocus
          isDisabled={isReadOnly}
          data-test-create-sql-import-input="name"
          {...name}
          onInput={(event) => {
            name.onInput(event);
            if (event.target instanceof HTMLInputElement) {
              onChange({ name: event.target.value });
            }
          }}
        />
        <TextArea
          label="Description"
          placeholder="Describe your import"
          isDisabled={isReadOnly}
          data-test-create-sql-import-input="description"
          {...description}
          onInput={(event) => {
            description.onInput(event);
            if (event.target instanceof HTMLTextAreaElement) {
              onChange({ description: event.target.value });
            }
          }}
        />
      </FormLayout>
    </Form>
  );
}
